import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import {
  MapPin, Phone, EnvelopeSimple, Globe, NavigationArrow, Wrench, CircleNotch,
  Star, MagnifyingGlass, ThumbsUp, Storefront, X, Package,
} from "@phosphor-icons/react";

import { api, getErrorMessage } from "@/lib/api";
import { CAPABILITIES, capLabel } from "@/lib/repairerMeta";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const AU_CENTER = [-25.2744, 133.7751];
const inputCls =
  "w-full bg-black/40 border border-neutral-700 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-[#73bf44] transition-colors placeholder:text-neutral-600";

function distKm(a, b) {
  const R = 6371;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

const STAR_SVG = (size) =>
  `<svg width="${size}" height="${size}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 1.8l2.95 6.36 6.85 1-4.96 4.83 1.17 6.86L12 17.6 5.99 20.85l1.17-6.86-4.96-4.83 6.85-1z" fill="#73bf44" stroke="#0a0a0a" stroke-width="1.3" stroke-linejoin="round"/></svg>`;

function makeIcon(r, highlight) {
  if (r.primary_centre) {
    const s = highlight ? 42 : 34;
    return L.divIcon({
      className: "",
      html: `<div style="filter:drop-shadow(0 2px 4px rgba(0,0,0,.6)) drop-shadow(0 0 7px rgba(115,191,68,.65))">${STAR_SVG(s)}</div>`,
      iconSize: [s, s],
      iconAnchor: [s / 2, s / 2],
      popupAnchor: [0, -s / 2],
    });
  }
  const size = highlight ? 26 : 20;
  return L.divIcon({
    className: "",
    html: `<div style="width:${size}px;height:${size}px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${
      highlight ? "#73bf44" : "#4f8f33"
    };border:2px solid #0a0a0a;box-shadow:0 2px 6px rgba(0,0,0,.55)"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, highlight ? -24 : -18],
  });
}

function popupHtml(r) {
  const loc = [r.suburb, r.state, r.postcode].filter(Boolean).join(" ");
  const dest = r.lat != null && r.lng != null ? `${r.lat},${r.lng}` : encodeURIComponent([r.address, r.suburb, r.state].filter(Boolean).join(", "));
  const primary = r.primary_centre
    ? `<div style="display:inline-block;margin-bottom:4px;font-size:10px;font-weight:700;color:#2b6e12;background:#e5f6da;padding:2px 6px;border-radius:99px">★ PRIMARY SERVICE CENTRE</div><br/>`
    : "";
  return `
    <div style="min-width:180px">
      ${primary}
      <div style="font-weight:700;color:#111;margin-bottom:2px">${r.business_name || ""}</div>
      <div style="color:#444;font-size:12px">${[r.address, loc].filter(Boolean).join(", ")}</div>
      ${r.phone ? `<div style="margin-top:6px;font-size:12px">📞 <a href="tel:${r.phone}" style="color:#2b6e12">${r.phone}</a></div>` : ""}
      <a href="https://www.google.com/maps/dir/?api=1&destination=${dest}" target="_blank" rel="noreferrer"
         style="display:inline-block;margin-top:8px;font-size:12px;font-weight:600;color:#2b6e12">Get directions →</a>
    </div>`;
}

function makeSupplierIcon(highlight) {
  const size = highlight ? 24 : 20;
  return L.divIcon({
    className: "",
    html: `<div style="width:${size}px;height:${size}px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#2b83ff;border:2px solid #0a0a0a;box-shadow:0 2px 6px rgba(0,0,0,.55)"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -18],
  });
}

function supplierPopupHtml(r) {
  const loc = [r.suburb, r.state, r.postcode].filter(Boolean).join(" ");
  const site = r.website ? (/^https?:\/\//.test(r.website) ? r.website : `https://${r.website}`) : "";
  return `
    <div style="min-width:180px">
      <div style="display:inline-block;margin-bottom:4px;font-size:10px;font-weight:700;color:#1e5fbf;background:#dbeafe;padding:2px 6px;border-radius:99px">SUPPLIER</div><br/>
      <div style="font-weight:700;color:#111;margin-bottom:2px">${r.name || ""}</div>
      <div style="color:#444;font-size:12px">${[r.address, loc].filter(Boolean).join(", ")}</div>
      ${r.phone ? `<div style="margin-top:6px;font-size:12px">📞 <a href="tel:${r.phone}" style="color:#1e5fbf">${r.phone}</a></div>` : ""}
      ${site ? `<a href="${site}" target="_blank" rel="noreferrer" style="display:inline-block;margin-top:8px;font-size:12px;font-weight:600;color:#1e5fbf">Visit website →</a>` : ""}
    </div>`;
}

// ---------------------------------------------------------------------------
// Submission form (application / recommendation) -> admin review queue
// ---------------------------------------------------------------------------
const APP_BLANK = {
  business_name: "", contact_name: "", address: "", suburb: "", state: "", postcode: "",
  phone: "", email: "", website: "", capabilities: [], brands: "", notes: "",
  submitter_name: "", submitter_email: "",
};

function SubmitDialog({ mode, open, onClose }) {
  const [form, setForm] = useState(APP_BLANK);
  const [saving, setSaving] = useState(false);
  const isRec = mode === "recommendation";

  useEffect(() => { if (open) setForm(APP_BLANK); }, [open]);

  const toggleCap = (key) =>
    setForm((f) => ({
      ...f,
      capabilities: f.capabilities.includes(key) ? f.capabilities.filter((k) => k !== key) : [...f.capabilities, key],
    }));

  const submit = async () => {
    if (!form.business_name.trim()) { toast.error("Please add the business name"); return; }
    setSaving(true);
    try {
      await api.post("/repairer-submissions", { ...form, type: mode });
      toast.success(isRec ? "Thanks! We'll review your recommendation." : "Thanks! We'll review your application and be in touch.");
      onClose();
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-white">
            {isRec ? "Recommend a repairer" : "Become an OTM preferred repairer"}
          </DialogTitle>
          <DialogDescription className="text-neutral-400">
            {isRec
              ? "Used a repairer you'd recommend? Send us their details and our team will review adding them to the network."
              : "Tell us about your business. Our team reviews every application before it appears on the map."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <input value={form.business_name} onChange={set("business_name")} className={inputCls} placeholder="Business name *" data-testid="submit-business" />
          {!isRec && (
            <input value={form.contact_name} onChange={set("contact_name")} className={inputCls} placeholder="Contact name" data-testid="submit-contact" />
          )}
          {!isRec && <input value={form.address} onChange={set("address")} className={inputCls} placeholder="Street address" data-testid="submit-address" />}
          <div className="grid grid-cols-3 gap-2">
            <input value={form.suburb} onChange={set("suburb")} className={inputCls} placeholder="Suburb" data-testid="submit-suburb" />
            <input value={form.state} onChange={set("state")} className={inputCls} placeholder="State" data-testid="submit-state" />
            <input value={form.postcode} onChange={set("postcode")} className={inputCls} placeholder="Postcode" data-testid="submit-postcode" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input value={form.phone} onChange={set("phone")} className={inputCls} placeholder="Phone" data-testid="submit-phone" />
            <input value={form.website} onChange={set("website")} className={inputCls} placeholder="Website" data-testid="submit-website" />
          </div>
          {!isRec && <input value={form.email} onChange={set("email")} className={inputCls} placeholder="Email" data-testid="submit-email" />}

          {!isRec && (
            <div>
              <div className="text-xs text-neutral-500 mb-1.5">Repair capabilities</div>
              <div className="grid grid-cols-2 gap-2">
                {CAPABILITIES.map((c) => {
                  const on = form.capabilities.includes(c.key);
                  return (
                    <label key={c.key} className={`flex items-center gap-2 cursor-pointer rounded-lg border px-2.5 py-1.5 text-xs transition-colors ${on ? "border-[#73bf44] bg-[#73bf44]/[0.08] text-white" : "border-neutral-800 text-neutral-400"}`}>
                      <input type="checkbox" checked={on} onChange={() => toggleCap(c.key)} className="accent-[#73bf44]" data-testid={`submit-cap-${c.key}`} />
                      {c.label}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {isRec && (
            <div className="grid grid-cols-2 gap-2">
              <input value={form.submitter_name} onChange={set("submitter_name")} className={inputCls} placeholder="Your name" data-testid="submit-your-name" />
              <input value={form.submitter_email} onChange={set("submitter_email")} className={inputCls} placeholder="Your email (optional)" data-testid="submit-your-email" />
            </div>
          )}

          <textarea value={form.notes} onChange={set("notes")} rows={3} className={inputCls} placeholder={isRec ? "Why do you recommend them? What did they fix?" : "Anything else (brands accredited, hours, etc.)"} data-testid="submit-notes" />

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="inline-flex items-center gap-2 border border-neutral-700 text-neutral-200 rounded-full px-4 py-2.5 text-sm hover:border-neutral-500">Cancel</button>
            <button onClick={submit} disabled={saving} data-testid="submit-send" className="inline-flex items-center gap-2 bg-[#73bf44] text-[#050505] rounded-full font-semibold px-5 py-2.5 text-sm hover:bg-[#5fa338] transition-colors disabled:opacity-60">
              {saving ? "Sending…" : "Submit for review"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Repairers() {
  const [repairers, setRepairers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [origin, setOrigin] = useState(null); // { lat, lng, label }
  const [locating, setLocating] = useState(false);
  const [pc, setPc] = useState("");
  const [pcLoading, setPcLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [filters, setFilters] = useState([]); // capability keys + "primary" + "supplier"
  const [dialog, setDialog] = useState(null); // "application" | "recommendation" | null

  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const markerRefs = useRef({});

  useEffect(() => {
    Promise.all([
      api.get("/repairers").then(({ data }) => data).catch(() => []),
      api.get("/suppliers").then(({ data }) => data).catch(() => []),
    ])
      .then(([reps, sups]) => { setRepairers(reps); setSuppliers(sups); })
      .finally(() => setLoading(false));
  }, []);

  // init map once — starts showing the whole of Australia
  useEffect(() => {
    if (mapRef.current || !mapDivRef.current) return;
    const map = L.map(mapDivRef.current, { scrollWheelZoom: true, zoomControl: true }).setView(AU_CENTER, 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  const toggleFilter = (key) =>
    setFilters((f) => (f.includes(key) ? f.filter((k) => k !== key) : [...f, key]));

  const supplierOn = filters.includes("supplier");
  const repairerFilters = useMemo(() => filters.filter((f) => f !== "supplier"), [filters]);
  const anyFilter = filters.length > 0;

  const list = useMemo(() => {
    const reps = repairers.map((r) => ({ ...r, _type: "repairer" }));
    const sups = suppliers.map((s) => ({ ...s, _type: "supplier", business_name: s.name }));
    const all = [...reps, ...sups]
      .filter((it) => {
        if (it._type === "supplier") return !anyFilter || supplierOn;
        // repairer
        if (!anyFilter) return true;
        if (supplierOn && repairerFilters.length === 0) return false; // suppliers-only view
        return repairerFilters.every((f) =>
          f === "primary" ? !!it.primary_centre : (it.capabilities || []).includes(f)
        );
      })
      .map((it) => ({
        ...it,
        _d: origin && it.lat != null && it.lng != null ? distKm(origin, { lat: it.lat, lng: it.lng }) : null,
      }));
    all.sort((a, b) => {
      if (a._d != null && b._d != null) return a._d - b._d;
      if (a._d != null) return -1;
      if (b._d != null) return 1;
      if (!!b.primary_centre !== !!a.primary_centre) return (b.primary_centre ? 1 : 0) - (a.primary_centre ? 1 : 0);
      return (a.business_name || "").localeCompare(b.business_name || "");
    });
    return all;
  }, [repairers, suppliers, anyFilter, supplierOn, repairerFilters, origin]);

  const nearest = list.find((r) => r._d != null) || null;
  const nearestId = nearest?.id || null;

  // (re)build markers whenever the visible list / origin changes
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    markerRefs.current = {};
    list.forEach((r) => {
      if (r.lat == null || r.lng == null) return;
      const isSupplier = r._type === "supplier";
      const icon = isSupplier ? makeSupplierIcon(r.id === nearestId) : makeIcon(r, r.id === nearestId);
      const m = L.marker([r.lat, r.lng], { icon, zIndexOffset: r.primary_centre ? 1000 : 0 })
        .bindPopup(isSupplier ? supplierPopupHtml(r) : popupHtml(r));
      m.on("click", () => setSelectedId(r.id));
      m.addTo(layer);
      markerRefs.current[r.id] = m;
    });
    if (origin) {
      L.circleMarker([origin.lat, origin.lng], {
        radius: 8, color: "#f59e0b", weight: 3, fillColor: "#f59e0b", fillOpacity: 0.5,
      }).addTo(layer).bindPopup(origin.label === "you" ? "You are here" : `Postcode ${origin.label}`);
    }
  }, [list, origin, nearestId]);

  // when origin (GPS or postcode) is set, zoom to it + nearest
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !origin) return;
    if (nearest && nearest.lat != null) {
      map.fitBounds([[origin.lat, origin.lng], [nearest.lat, nearest.lng]], { padding: [60, 60], maxZoom: 11 });
      const mk = markerRefs.current[nearest.id];
      if (mk) setTimeout(() => mk.openPopup(), 400);
    } else {
      map.setView([origin.lat, origin.lng], 8);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin]);

  const focus = useCallback((r) => {
    setSelectedId(r.id);
    const map = mapRef.current;
    const mk = markerRefs.current[r.id];
    if (map && mk && r.lat != null) {
      map.flyTo([r.lat, r.lng], 11, { duration: 0.6 });
      setTimeout(() => mk.openPopup(), 500);
    }
  }, []);

  const findClosest = () => {
    if (!navigator.geolocation) {
      toast.error("Location services aren't available on this device.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: "you" });
        setLocating(false);
        toast.success("Found you — showing your closest repairers.");
      },
      () => {
        setLocating(false);
        toast.error("Couldn't get your location. Please allow location access and try again.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const searchPostcode = async (e) => {
    e?.preventDefault?.();
    const code = pc.trim();
    if (!code) return;
    setPcLoading(true);
    try {
      const { data } = await api.get(`/geocode`, { params: { postcode: code } });
      setOrigin({ lat: data.lat, lng: data.lng, label: code });
      toast.success(`Showing repairers near ${code}.`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't find that postcode."));
    } finally {
      setPcLoading(false);
    }
  };

  const clearOrigin = () => { setOrigin(null); setPc(""); mapRef.current?.setView(AU_CENTER, 4); };

  const filterChips = [
    { key: "supplier", label: "Suppliers" },
    { key: "primary", label: "Primary Service Centre" },
    ...CAPABILITIES,
  ];

  return (
    <div className="h-full overflow-y-auto" data-testid="repairers-page">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <div className="mb-6">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#73bf44] mb-2">Repair Network</div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter text-white">Find a Repairer</h1>
          <p className="text-neutral-400 mt-2 max-w-2xl">
            OTM preferred repairers across Australia. Search by postcode, use your location, or filter by what you need.
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-sm text-neutral-300">
            <span className="inline-flex items-center gap-1.5"><Star size={13} weight="fill" className="text-[#73bf44]" /> Primary Service Centre</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#4f8f33]" /> Repairer</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#2b83ff]" /> Supplier (call the brand direct)</span>
          </div>
        </div>

        {/* search + locate */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <form onSubmit={searchPostcode} className="flex-1 flex gap-2" data-testid="repairers-postcode-form">
            <div className="relative flex-1">
              <MagnifyingGlass size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                value={pc}
                onChange={(e) => setPc(e.target.value)}
                inputMode="numeric"
                placeholder="Enter a postcode (e.g. 4207)"
                data-testid="repairers-postcode-input"
                className={`${inputCls} pl-9`}
              />
            </div>
            <button type="submit" disabled={pcLoading} data-testid="repairers-postcode-search"
              className="inline-flex items-center gap-2 border border-neutral-700 text-white rounded-lg px-4 py-2.5 text-sm hover:border-[#73bf44] transition-colors disabled:opacity-60">
              {pcLoading ? <CircleNotch size={17} className="animate-spin" /> : "Search"}
            </button>
          </form>
          <button
            onClick={findClosest}
            disabled={locating}
            data-testid="repairers-find-closest"
            className="inline-flex items-center justify-center gap-2 bg-[#73bf44] text-[#050505] rounded-lg font-semibold px-5 py-2.5 hover:bg-[#5fa338] transition-colors disabled:opacity-60"
          >
            {locating ? <CircleNotch size={18} className="animate-spin" /> : <NavigationArrow size={18} weight="fill" />}
            {locating ? "Locating…" : "Find my closest"}
          </button>
        </div>

        {/* filter chips */}
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="repairers-filters">
          {filterChips.map((c) => {
            const on = filters.includes(c.key);
            const isPrimary = c.key === "primary";
            const isSupplier = c.key === "supplier";
            return (
              <button
                key={c.key}
                onClick={() => toggleFilter(c.key)}
                data-testid={`repairers-filter-${c.key}`}
                className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${
                  on
                    ? isSupplier
                      ? "bg-[#2b83ff] text-white border-[#2b83ff]"
                      : "bg-[#73bf44] text-[#050505] border-[#73bf44]"
                    : "border-neutral-800 text-neutral-400 hover:text-white hover:border-neutral-600"
                }`}
              >
                {isPrimary && <Star size={12} weight="fill" />}
                {isSupplier && <Package size={12} weight="fill" />}
                {c.label}
              </button>
            );
          })}
          {(filters.length > 0 || origin) && (
            <button onClick={() => { setFilters([]); clearOrigin(); }} data-testid="repairers-clear" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-white ml-1">
              <X size={13} /> Clear
            </button>
          )}
        </div>

        <div className="grid lg:grid-cols-[380px_1fr] gap-5">
          {/* list */}
          <div className="order-2 lg:order-1 space-y-3 lg:max-h-[560px] lg:overflow-y-auto lg:pr-1" data-testid="repairers-list">
            {loading ? (
              <div className="text-neutral-600">Loading repairers…</div>
            ) : list.length === 0 ? (
              <div className="text-neutral-500 border border-dashed border-neutral-800 rounded-2xl py-14 text-center flex flex-col items-center gap-3">
                <Wrench size={34} className="text-neutral-700" weight="duotone" />
                {repairers.length + suppliers.length === 0 ? "Nothing listed yet." : "Nothing matches your filters."}
              </div>
            ) : (
              list.map((r) => {
                const active = r.id === selectedId;
                const isNearest = r.id === nearestId;
                if (r._type === "supplier") {
                  return (
                    <button
                      key={r.id}
                      onClick={() => focus(r)}
                      data-testid="supplier-card"
                      className={`w-full text-left rounded-2xl border p-4 transition-colors ${
                        active ? "border-[#2b83ff] bg-[#2b83ff]/[0.08]" : "border-[#2b83ff]/30 bg-[#2b83ff]/[0.04] hover:border-[#2b83ff]/60"
                      }`}
                    >
                      <div className="inline-flex items-center gap-1 text-[0.6rem] font-bold uppercase tracking-wider text-[#5aa0ff] mb-1.5">
                        <Package size={11} weight="fill" /> Supplier
                      </div>
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-heading font-semibold text-white">{r.name}</div>
                        {r._d != null && (
                          <span data-testid="repairer-distance" className={`shrink-0 text-[0.65rem] font-bold px-2 py-0.5 rounded-full ${isNearest ? "bg-[#2b83ff] text-white" : "bg-neutral-800 text-neutral-300"}`}>
                            {r._d < 10 ? r._d.toFixed(1) : Math.round(r._d)} km
                          </span>
                        )}
                      </div>
                      {(r.address || r.suburb || r.state || r.postcode) && (
                        <div className="flex items-start gap-1.5 text-sm text-neutral-400 mt-1">
                          <MapPin size={14} className="text-neutral-500 mt-0.5 shrink-0" />
                          <span>{[r.address, r.suburb, r.state, r.postcode].filter(Boolean).join(", ")}</span>
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                        {r.phone && (
                          <a href={`tel:${r.phone}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1.5 text-sm text-[#5aa0ff] hover:underline">
                            <Phone size={13} /> {r.phone}
                          </a>
                        )}
                        {r.email && (
                          <a href={`mailto:${r.email}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-white">
                            <EnvelopeSimple size={13} /> Email
                          </a>
                        )}
                        {r.website && (
                          <a href={/^https?:\/\//.test(r.website) ? r.website : `https://${r.website}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-white">
                            <Globe size={13} /> Website
                          </a>
                        )}
                        {r.lat == null && <span className="text-[0.7rem] text-amber-500/80">Map location pending</span>}
                      </div>
                    </button>
                  );
                }
                return (
                  <button
                    key={r.id}
                    onClick={() => focus(r)}
                    data-testid="repairer-card"
                    className={`w-full text-left rounded-2xl border p-4 transition-colors ${
                      active ? "border-[#73bf44] bg-[#73bf44]/[0.06]"
                      : r.primary_centre ? "border-[#73bf44]/40 bg-[#73bf44]/[0.03] hover:border-[#73bf44]/70"
                      : "border-neutral-800 bg-[#0F0F0F] hover:border-neutral-600"
                    }`}
                  >
                    {r.primary_centre && (
                      <div className="inline-flex items-center gap-1 text-[0.6rem] font-bold uppercase tracking-wider text-[#73bf44] mb-1.5" data-testid="repairer-primary-badge">
                        <Star size={11} weight="fill" /> Primary Service Centre
                      </div>
                    )}
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-heading font-semibold text-white">{r.business_name}</div>
                      {r._d != null && (
                        <span
                          data-testid="repairer-distance"
                          className={`shrink-0 text-[0.65rem] font-bold px-2 py-0.5 rounded-full ${
                            isNearest ? "bg-[#73bf44] text-[#050505]" : "bg-neutral-800 text-neutral-300"
                          }`}
                        >
                          {r._d < 10 ? r._d.toFixed(1) : Math.round(r._d)} km
                        </span>
                      )}
                    </div>
                    <div className="flex items-start gap-1.5 text-sm text-neutral-400 mt-1">
                      <MapPin size={14} className="text-neutral-500 mt-0.5 shrink-0" />
                      <span>{[r.address, r.suburb, r.state, r.postcode].filter(Boolean).join(", ") || "—"}</span>
                    </div>
                    {(r.capabilities || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {r.capabilities.map((k) => (
                          <span key={k} className="text-[0.6rem] font-medium px-1.5 py-0.5 rounded bg-neutral-800 text-neutral-300">{capLabel(k)}</span>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                      {r.phone && (
                        <a href={`tel:${r.phone}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1.5 text-sm text-[#73bf44] hover:underline">
                          <Phone size={13} /> {r.phone}
                        </a>
                      )}
                      {r.email && (
                        <a href={`mailto:${r.email}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-white">
                          <EnvelopeSimple size={13} /> Email
                        </a>
                      )}
                      {r.website && (
                        <a href={/^https?:\/\//.test(r.website) ? r.website : `https://${r.website}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-white">
                          <Globe size={13} /> Website
                        </a>
                      )}
                      {r.lat == null && <span className="text-[0.7rem] text-amber-500/80">Map location pending</span>}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* map */}
          <div className="order-1 lg:order-2">
            <div
              ref={mapDivRef}
              data-testid="repairers-map"
              className="w-full h-[360px] md:h-[520px] rounded-2xl overflow-hidden border border-neutral-800 z-0"
            />
          </div>
        </div>

        {/* Submit actions */}
        <div className="grid md:grid-cols-2 gap-4 mt-8">
          <div className="rounded-2xl border border-[#73bf44]/30 bg-gradient-to-b from-[#161a12] to-[#121212] p-6" data-testid="repairer-apply">
            <div className="w-11 h-11 rounded-xl bg-[#73bf44]/10 border border-[#73bf44]/30 flex items-center justify-center mb-3">
              <Storefront size={22} className="text-[#73bf44]" weight="duotone" />
            </div>
            <h3 className="font-heading text-lg font-bold text-white">Become an OTM preferred repairer</h3>
            <p className="text-sm text-neutral-400 mt-1">
              Are you a caravan repairer keen to join the On The Move Repair Network? Submit your details and our team will review your application.
            </p>
            <button onClick={() => setDialog("application")} data-testid="repairer-apply-open"
              className="inline-flex items-center gap-2 mt-4 bg-[#73bf44] text-[#050505] rounded-full font-semibold px-5 py-2.5 text-sm hover:bg-[#5fa338] transition-colors">
              <Storefront size={16} weight="fill" /> Apply to join
            </button>
          </div>

          <div className="rounded-2xl border border-neutral-800 bg-[#0F0F0F] p-6" data-testid="repairer-recommend">
            <div className="w-11 h-11 rounded-xl bg-white/[0.04] border border-neutral-800 flex items-center justify-center mb-3">
              <ThumbsUp size={22} className="text-[#73bf44]" weight="duotone" />
            </div>
            <h3 className="font-heading text-lg font-bold text-white">Recommend a repairer</h3>
            <p className="text-sm text-neutral-400 mt-1">
              Used a repairer you'd recommend? Send us their details and we'll review adding them to the network.
            </p>
            <button onClick={() => setDialog("recommendation")} data-testid="repairer-recommend-open"
              className="inline-flex items-center gap-2 mt-4 border border-neutral-700 text-white rounded-full font-semibold px-5 py-2.5 text-sm hover:border-[#73bf44] transition-colors">
              <ThumbsUp size={16} weight="fill" /> Recommend one
            </button>
          </div>
        </div>
      </div>

      <SubmitDialog mode={dialog === "recommendation" ? "recommendation" : "application"} open={!!dialog} onClose={() => setDialog(null)} />
    </div>
  );
}
